import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { AnnouncementBanner } from '@helpcenter/announcement-banner';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import * as bp from '@helpcenter/styles/src/breakpoints';
import * as mixins from '@helpcenter/styles/src/mixins';
import { sizes } from '@helpcenter/styles/src/sizes-viewport';
import FallbackAnnouncement from 'assets/images/fallback-announcement.svg';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { ElementMargins } from 'view/common/element-root/element-root';
import { PaperContent } from 'view/layout/paper';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { lightenHexColor } from '@atlassian/help-center-common-util/ligthen-hex-color';
import { removeHyperlinks } from '@atlassian/help-center-common-util/parse';
import type { helpCenterAnnouncementFragment$key } from './__generated__/helpCenterAnnouncementFragment.graphql';
import messages from './messages';

export interface AnnouncementViewerProps {
    themeColor: string;
    announcementElement: helpCenterAnnouncementFragment$key;
    hasTopBarBeenSplit?: boolean;
}

export const HelpCenterAnnouncement: React.FC<AnnouncementViewerProps> = ({
    themeColor,
    announcementElement,
    hasTopBarBeenSplit,
}) => {
    const data = useFragment(
        graphql`
            fragment helpCenterAnnouncementFragment on HelpLayoutAnnouncementElement {
                header
                message
            }
        `,
        announcementElement
    );
    di(useIsPageInEditMode);
    di(useIsPageInPreviewMode);
    di(isIncreasedFlexibilityEnabled);
    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();
    const { formatMessage } = useIntl();

    let backgroundColor: string | undefined = undefined;

    if (isIncreasedFlexibilityEnabled()) {
        backgroundColor = lightenHexColor(themeColor, 0.85) || themeColor; // since lightenHexColor has null return we have themeColor to make non nullable
    }

    const announcement = { ...data };
    //fallback UI in edit mode
    if (isPageInEditMode && !isPageInPreviewMode && !announcement.header && !announcement.message) {
        const message = isIncreasedFlexibilityEnabled()
            ? messages.emptyStateAnnouncementDescription
            : messages.emptyStateDescription;
        return (
            <Box
                xcss={nonDraggableStyle}
                style={{
                    backgroundColor: backgroundColor ?? 'transparent',
                }}
            >
                <EmptyState
                    header=""
                    maxImageWidth={250}
                    description={formatMessage(message, {
                        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                    })}
                    imageUrl={FallbackAnnouncement}
                    width="wide"
                />
            </Box>
        );
    }
    //Removing all hyperlinks from announcement message when page is in edit mode
    if (isPageInEditMode && !!announcement.message) {
        announcement.message = removeHyperlinks(announcement.message);
    }

    if (isIncreasedFlexibilityEnabled()) {
        if (!announcement.header && !announcement.message) {
            return null;
        }
        return (
            <ElementRoot elementWidth={ElementWidth.FULL_WIDTH} elementMargins={ElementMargins.COMPACT} removePadding>
                <Box
                    testId={'help-center-announcement-background'}
                    xcss={
                        hasTopBarBeenSplit
                            ? announcementBackgroundContainer
                            : announcementBackgroundContainerWithoutTopBarSplit
                    }
                    style={{ backgroundColor: backgroundColor ?? 'transparent' }}
                >
                    <AnnouncementBanner
                        actionSubjectId="helpCenterAnnouncementBanner"
                        removeBgColor
                        header={announcement.header || ''}
                        messageHtml={announcement.message || ''}
                        InnerContainerComponent={AnnouncementInnerContainer}
                        isLayoutInEditMode={isPageInEditMode}
                        isLayoutInPreviewMode={isPageInPreviewMode}
                    />
                </Box>
            </ElementRoot>
        );
    }

    return (
        <>
            {!!announcement && (
                <AnnouncementBanner
                    actionSubjectId="helpCenterAnnouncementBanner"
                    removeBgColor
                    header={announcement.header || ''}
                    messageHtml={announcement.message || ''}
                    InnerContainerComponent={AnnouncementInnerContainer}
                    isLayoutInEditMode={isPageInEditMode}
                    isLayoutInPreviewMode={isPageInPreviewMode}
                />
            )}
        </>
    );
};
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AnnouncementInnerContainer = styled(PaperContent)`
    ${mixins.borderBox};
    width: 100%;
    margin: 0 auto;
    padding: 0 ${token('space.400', '32px')};

    ${bp.fromSmall.css`
    max-width: ${sizes.small}px;
    /* We deliberately use padding-right/left so we can override top/bottom easily. */
    padding-right: ${token('space.600', '48px')};
    padding-left: ${token('space.600', '48px')};
`};
`;
const nonDraggableStyle = xcss({
    pointerEvents: 'none',
});

const announcementBackgroundContainer = xcss({
    width: '100%',
    borderRadius: 'radius.large',
});

const announcementBackgroundContainerWithoutTopBarSplit = xcss({
    width: '100%',
});
